<template>
  <div id="externalKnowledgeBase">

    <div class="vo-external-card">

      <div class="vo-body">

        <div class="vo-title">{{ title }}</div>


        <div class="section-title">How to create a ticket</div>
        <div class="section-body">
          To create a ticket, complete the following:
            <ol>
              <li>Select <strong>TICKETS</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu.</strong></li>
              <li>Click the <strong>+</strong> button.</li>
              <li>Select the appropriate property from the <strong>Property</strong> drop down menu.</li>
              <li>Enter the appropriate unit in the <strong>Unit Search Bar</strong>.</li>
              <li>Select all ticket tags that apply to the issue.</li>
              <li>Enter an appropriate title and description.</li>
              <li>(Optional) Select <strong>SELECT IMAGES</strong> to upload any images of the issue.</li>
              <li>Check the <strong>I CONSENT</strong> checkbox.</li>
              <li>Click <strong>CREATE</strong>.</li>
            </ol>
        </div>


        <div class="section-title">How to remove a ticket</div>
        <div class="section-body">You cannot remove tickets.</div>


        <div class="section-title">How do I view tickets?</div>
        <div class="section-body">
          To view tickets, complete the following:
          <ol>
            <li>Select <strong>TICKETS</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu</strong>.</li>
            <li>Select the ticket you would like to view.</li>
          </ol>
        </div>


        <div class="section-title">How do I view resolved tickets?</div>
        <div class="section-body">
          The tickets tab filters <strong>Unresolved</strong> tickets by default, which include every ticket that is not marked as <strong>Resolved</strong>.  To view resolved tickets, complete the following:
          <ol>
            <li>Select <strong>TICKETS</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu</strong>.</li>
            <li>Under Statuses, select <strong>Resolved</strong>.</li>
          </ol>
        </div>


        <div class="section-title">How to obtain a Vilio account</div>
        <div class="section-body">
          To obtain a Vilio account, you have to receive an invite from someone with invite permissions at the property you would like to join. They will require an email address to send the invite. You can create your account as soon as you receive the invitation email. To create your account, complete the following:
          <ol>
            <li>Open the invitation email and click <strong>GET STARTED</strong>.</li>
            <li>Fill in your <strong>Profile</strong> information.</li>
            <li>Select the notifications you would like to receive. You can choose whether to have push notification sent to your smart device (requires the Vilio application to be installed), sent to your email address, both, or neither.</li>
            <li>Note: Your notification preferences can be changed in Settings.</li>
            <li>Read the <strong>Terms of Use &amp; Privacy Policy.</strong></li>
            <li>Click the checkbox under <strong>Terms of Use &amp;Privacy Policy</strong> to indicate you have read and agree to Vilio's Terms of Use and Privacy Policy.</li>
            <li>Click <strong>COMPLETE</strong> > <strong>LET'S GO</strong>.</li>
          </ol>
        </div>


        <div class="section-title">How to reset your account password</div>
        <div class="section-body">
          You can recover your Vilio account in the event that you are unable to remember your account password.  To recover your account, complete the following:
          <ol>
            <li>Navigate to <strong><a href="appUrl" target="_blank">{{ appUrl }}</a></strong> or open the Vilio application on your smart device.</li>
            <li>Click <strong>FORGOT PASSWORD</strong>.</li>
            <li>Enter the email address associated with your account.</li>
            <li>Click <strong>SEND</strong>.</li>
            <li>Open the password reset email and click <strong>RESET PASSWORD</strong>.</li>
            <li>Enter the same password in both text fields and click <strong>RESET PASSWORD</strong > <strong>GOT IT</strong>.</li>
          </ol>
          You can now log into your Vilio account with your new password.
        </div>


        <div class="section-title">How to remove a role from a user</div>
        <div class="section-body">
          To remove a role from a user, complete the following:

          <ol>
            <li>Click <strong>PEOPLE</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu</strong>.</li>
            <li>Select the user you would like to edit.</li>
            <li>Under <strong>ROLES</strong>, click the <strong>Delete</strong> icon.</li>
          </ol>

          <strong>Note:</strong> Vilio users must have at least one role to remain associated with your company.
        </div>


        <div class="section-title">How to remove user access</div>
        <div class="section-body">
          You can remove a user's access to your property or company by removing all of their roles.
        </div>          


        <div class="section-title">My building has Vilio but I cannot create an account.  What do I do?</div>
        <div class="section-body">
          Users can only be added by someone with the <strong>Users and Invites</strong> permission.
        </div>


        <div class="section-title">Why can't I create or view tickets?</div>
        <div class="section-body">
          Tickets can only be created by someone with the Ticket Creator permission.
        </div>


        <div class="section-title">Who has access to view a ticket?</div>
        <div class="section-body">
          The following people can view a ticket:
          <ul>
            <li>The user who created the ticket.</li>
            <li>Any users assigned to the ticket.</li>
            <li>Any users with the <strong>Ticket Admin</strong> permission.</li>
          </ul>

          Users also have to be associated with the property. 
        </div>


        <div class="section-title">Why can't I upload my image to a ticket?</div> 
        <div class="section-body">Tickets only support .jpg, .png, and .bmp images.  Make sure the image you are attempting to upload is one of these file types.</div>
        


        <div class="section-title">How to edit your notification settings</div>
        <div class="section-body">
          To edit your Vilio notification settings, complete the following:
          <ol>
            <li>Click <strong>Account Profile</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu</strong>.</li>
            <li>Click <strong>Settings</strong>.</li>
            <li>Under <strong>NOTIFICATION SETTINGS</strong>, select the notifications you would like to receive.</li>
            <li>Click <strong>SAVE</strong>.</li>
          </ol>
        </div>           
          
        <div class="section-title">How to disable notifications</div>
        <div class="section-body">
          To disable Vilio notifications, complete the following:
          <ol>
            <li>Click <strong>Account Profile</strong> from the <strong>Top Bar</strong> or <strong>Navigation Menu</strong>.</li>
            <li>Click <strong>Settings</strong>.</li>
            <li>Under <strong>NOTIFICATION SETTINGS</strong>, uncheck each checkbox.</li>
            <li>Click <strong>SAVE</strong>.</li>
          </ol>
        </div>

        <div class="section-title">What devices support Vilio?</div>
        <div class="section-body">Vilio can be accessed via a supported web browser or the mobile application.  The mobile application is available for download on the Apple App Store for iOS devices and the Google Play Store for Android devices.</div>  


        <div class="section-title">What web browsers does Vilio support?</div>
        <div class="section-body">
          Vilio supports the following web browsers:
          <ul>
            <li>Chrome (<a href="https://app.vilio.co/browser/chrome" target="_blank">https://app.vilio.co/browser/chrome</a>)</li>
            <li>Firefox (<a href="https://app.vilio.co/browser/firefox" target="_blank">https://app.vilio.co/browser/firefox</a>)</li>
            <li>Edge (<a href="https://app.vilio.co/browser/edge" target="_blank">https://app.vilio.co/browser/edge</a>)</li>
            <li>Safari (<a href="https://app.vilio.co/browser/safari" target="_blank">https://app.vilio.co/browser/safari</a>)</li> 
          </ul>
        </div>


        <vo-external-header :title="title"></vo-external-header>
      </div>

    </div>

  </div>
</template>


<script>

  export default {
    data () {
      return {
        title: 'Vilio Knowledge Base'
      }
    },

    computed: {
      
      appUrl () {
        return APP_URL;
      },

      version () {
        return VERSION;
      }
    }
  }

</script>

<style lang="scss">

  @import '../css/global.scss';

  #externalKnowledgeBase {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    @include scrollable;

    .vo-title {
      margin-bottom: 0;
    }

    a {
      color: $primary;
    }

    .section-title {
      @include fontPrimary400();
      text-decoration: underline;

      color: $black54;
      font-size: 16px;
      margin: 40px 0 16px;
    }
    
    .section-body {
      margin: 0 0 0 8px;
      color: $black87;
    }

    ul, ol {
      margin: 16px 0 16px 24px;
      // list-style-type: none;

      li {
        margin: 4px 0 0;
        padding: 0 0 0 8px;
      }
    }

  }

  @media screen and (max-width: 780px) {
  
    #externalKnowledgeBase {

      background: $lightGreen !important;

      .vo-title {
        display: none;
      }
    
    }
  }

</style>


// WEBPACK FOOTER //
// src/components/ExternalKnowledgeBase.vue